import { useReduxState } from 'hooks';
import {
  ContentLabel,
  ContentValue,
  FlexContainer,
  RichTextValue,
  User
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';
import { PathPicture, PathsTileHeader } from 'components/paths';

import type {
  CompetencyInProduct,
  Path,
  PathStep,
  PathStepProgress,
  User as UserType,
  UserWithBasicData
} from 'shared-types';

interface Props {
  isUserPage: boolean;
  loading: boolean;
  path: Pick<
    Path,
    | 'certificateAllowDownloading'
    | 'customAttributes'
    | 'description'
    | 'endDate'
    | 'id'
    | 'picture'
    | 'picturePosition'
    | 'title'
    | 'startDate'
  > & {
    competencies: CompetencyInProduct[];
    owners: UserWithBasicData[];
    progressData: PathStepProgress;
    steps: Pick<PathStep, 'id'>[];
  };
  targetUser: Pick<UserType, 'id'>;
}

const ProfilePathsPathSummary = ({
  isUserPage,
  loading,
  path,
  targetUser
}: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  return (
    <div className="summary">
      <Box
        loading={loading}
        title={
          <BoxBackLink
            as={isUserPage ? `/users/${targetUser.id}/paths` : '/profile/paths'}
            href={isUserPage ? '/users/[userId]/paths' : '/profile/paths'}
            text={messages.pathsAllPaths}
          />
        }
      >
        {path ? (
          <div className="inner">
            <div className="picture">
              <PathPicture
                picture={path.picture}
                picturePosition={path.picturePosition}
                size={960}
                style={{
                  borderRadius: theme.cardBorderRadius,
                  height: 140
                }}
              />
            </div>

            <div className="header">
              <PathsTileHeader
                numberOfSteps={path.steps?.length}
                path={path}
                titleLink={`/paths/${path.id}`}
              />
            </div>

            {path.description ? (
              <div className="description">
                <RichTextValue html={path.description} />
              </div>
            ) : null}

            <div className="details">
              <DisplayAttributeInProductPage
                customAttributes={path.customAttributes}
                inProfile={true}
                labelSpacing={30}
                position="at start"
                productType="path"
              />

              {!serverConfig.productHideOwner && (
                <ContentLabel label={messages.commonOwners} spacing={30}>
                  <ContentValue>
                    <div className="owners">
                      {path.owners.map((owner) => (
                        <User
                          key={owner.id}
                          size="small"
                          showEmail={false}
                          showEmailTooltip={false}
                          titleStyle={{ fontSize: '1.4rem' }}
                          user={owner}
                        />
                      ))}
                    </div>
                  </ContentValue>
                </ContentLabel>
              )}

              {path.competencies?.length ? (
                <ContentLabel
                  label={messages.competenciesGranted}
                  spacing={30}
                  style={{ whiteSpace: 'nowrap' }}
                  tooltip={messages.pathsCompetenciesGrantedInfo}
                >
                  <ContentValue>
                    <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                      {path.competencies.map((competency) => (
                        <CompetenciesProductBadge
                          key={competency.id}
                          competency={competency}
                        />
                      ))}
                    </FlexContainer>
                  </ContentValue>
                </ContentLabel>
              ) : null}

              <DisplayAttributeInProductPage
                customAttributes={path.customAttributes}
                inProfile={true}
                labelSpacing={30}
                productType="path"
              />
            </div>
          </div>
        ) : null}
      </Box>

      <style jsx>{`
        .info {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
        }
        .header {
          margin-top: 15px;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          margin-top: 15px;
        }
        .details {
          margin-top: 30px;
        }
        .owners {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .categories {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </div>
  );
};

export default ProfilePathsPathSummary;
